import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { IPlatformHomeMagicBar } from '@shared/components/platform-tool/platform-tool-magic-bar/models/magic-bar-models';
import { HomeSideBarItem } from '@shared/models/platform-tool-models';
import { PlatformCommand } from '@shared/commands/command-base';
import { Api } from '@shared/constants/common-field.constants';
import { Configuration } from '@shared/models/configuration';

import { CommandService } from '@core/services/command.service';
import { FileManagementService } from '@core/services/file-management.service';
import { SnackBarService } from '@core/services/snack-bar.service';

import { FlexSheetService } from '@features/spreadsheet/services/flex-sheet.service';
import { FlexSheetNavigationService } from '@features/spreadsheet/services/flex-sheet-navigation.service';

export type TabKey = 'validation';

export interface TransmissionErrorMetaData {
  WorkbookId?: string;
  WorksheetId?: string;
  Row?: number;
  Column?: number;
  RowEnd?: number;
  ColumnEnd?: number;
  GridRow?: number;
  GridColumn?: number;
  // Human-readable cell labels for the "Sheet!A1" link text (navigation uses the numeric Row/Column above).
  SheetName?: string;
  CellAddress?: string;
  DataPath?: string;
  HasExcelMappingException?: boolean;
  TicketId?: string;
  TicketDisplayId?: string;
  TicketTitle?: string;
  TicketCreationFailed?: boolean;
  TicketCreationReason?: string;
  [key: string]: unknown;
}

export interface TransmissionError {
  id: string;
  errorType: string;
  message: string;
  exceptionDetail: string;
  errorSequenceId: number;
  originalValidationMessage?: string;
  metaData: TransmissionErrorMetaData;
}

interface TabState {
  errors: TransmissionError[];
  isLoading: boolean;
  hasGenerated: boolean;
  isProcessComplete: boolean;
}

@Component({
  selector: 'app-scenario-form-validation-error',
  standalone: false,
  templateUrl: './scenario-form-validation-error.component.html',
  styleUrl: './scenario-form-validation-error.component.scss'
})
export class ScenarioFormValidationErrorComponent implements IPlatformHomeMagicBar, OnInit, OnDestroy {

  homeSideBarItem: HomeSideBarItem;

  reportWorkbookSetId: string;
  reportKey: string;
  // Row id of the country's report; lets the backend run country-specific rules. Empty for the EU master.
  itemId: string;

  private tabState: Record<TabKey, TabState> = {
    validation: { errors: [], isLoading: false, hasGenerated: false, isProcessComplete: false }
  };

  private tabConfig: Record<TabKey, { label: string; commandName: string; responseKey: string }> = {
    validation: { label: 'XML Validation', commandName: 'FilingDataValidateCommand', responseKey: 'ValidationErrors' }
  };

  activeTab: TabKey = 'validation';

  isDownloading = false;
  unSub$ = new Subject<void>();
  @Output() rowClick = new EventEmitter<TransmissionError>();

  private get current(): TabState { return this.tabState[this.activeTab]; }

  get validationErrors(): TransmissionError[] { return this.current.errors; }
  set validationErrors(v: TransmissionError[]) { this.current.errors = v; }

  get isLoading(): boolean { return this.current.isLoading; }
  set isLoading(v: boolean) { this.current.isLoading = v; }

  get hasGenerated(): boolean { return this.current.hasGenerated; }
  set hasGenerated(v: boolean) { this.current.hasGenerated = v; }

  get isProcessComplete(): boolean { return this.current.isProcessComplete; }
  set isProcessComplete(v: boolean) { this.current.isProcessComplete = v; }

  constructor(
    private commandService: CommandService,
    private fileManagementService: FileManagementService,
    private snackBarService: SnackBarService,
    private flexSheetService: FlexSheetService,
    private flexSheetNavigationService: FlexSheetNavigationService,
  ) { }

  ngOnInit(): void {
    const fv = this.homeSideBarItem?.fieldValues ?? {};
    this.reportWorkbookSetId = fv['ReportWorkbookSetId'];
    this.reportKey = fv['ReportKey'];
    this.itemId = fv['ItemId'];
  }

  ngOnDestroy(): void {
    this.unSub$.next();
    this.unSub$.complete();
  }

  // --- Tabs ---------------------------------------------------------------

  get visibleTabs(): TabKey[] { return ['validation']; }

  tabLabel(tab: TabKey): string { return this.tabConfig[tab].label; }

  get selectedTabIndex(): number {
    const idx = this.visibleTabs.indexOf(this.activeTab);
    return idx >= 0 ? idx : 0;
  }

  onTabIndexChange(idx: number): void {
    const next = this.visibleTabs[idx];
    if (next && next !== this.activeTab) {
      this.setActiveTab(next);
    }
  }

  setActiveTab(tab: TabKey): void {
    this.unSub$.next();
    this.unSub$.complete();
    this.unSub$ = new Subject<void>();
    this.isLoading = false;
    this.isDownloading = false;
    this.activeTab = tab;
  }

  // --- Load / refresh -----------------------------------------------------

  loadValidationErrors(): void {
    if (!this.reportWorkbookSetId) {
      this.validationErrors = [];
      return;
    }
    this.isLoading = true;
    const config = this.tabConfig[this.activeTab];
    const command = new PlatformCommand(config.commandName, Api.Spreadsheet);
    command.setValue('ReportWorkbookSetId', this.reportWorkbookSetId);
    command.setValue('ReportKey', this.reportKey);
    command.setValue('ItemId', this.itemId);

    this.commandService.executePlatformCommand(command).pipe(
      takeUntil(this.unSub$)
    ).subscribe({
      next: (response) => {
        const fv = response?.fieldValues ?? {};
        const isSuccess = fv['IsSuccess'] === true;

        if (!isSuccess) {
          this.validationErrors = [
            this.normalizeError({
              MetaData: {
                HasExcelMappingException: true,
                TicketId: fv['TicketId'],
                TicketDisplayId: fv['TicketDisplayId'],
                TicketTitle: fv['TicketTitle'],
              }
            })
          ];
          this.isProcessComplete = false;
        } else {
          const raw = fv[config.responseKey];
          this.validationErrors = Array.isArray(raw)
            ? raw.map((e: any) => this.normalizeError(e))
            : [];
          this.isProcessComplete = true;
        }
        this.isLoading = false;
        this.hasGenerated = true;
      },
      error: () => {
        this.validationErrors = [
          this.normalizeError({ MetaData: { HasExcelMappingException: true } })
        ];
        this.isProcessComplete = false;
        this.isLoading = false;
        this.hasGenerated = true;
      }
    });
  }

  refresh(): void {
    this.loadValidationErrors();
  }

  // --- Download -----------------------------------------------------------

  downloadValidationErrors(): void {
    if (this.isDownloading) return;
    if (!this.hasValidationErrors) {
      // Only reachable if the Download button's [disabled] binding is bypassed.
      if (!this.hasGenerated) {
        this.snackBarService.showWarning('Generate the validation report first.');
      } else {
        this.snackBarService.showWarning('There are no validation errors to download.');
      }
      return;
    }
    this.isDownloading = true;
    const command = new PlatformCommand('DownloadPublicCbCFormErrorCommand', Api.Spreadsheet);
    command.setValue("ReportWorkbookSetId", this.reportWorkbookSetId);

    this.commandService.executePlatformCommand(command).pipe(takeUntil(this.unSub$)).subscribe({
      next: commandResponse => {
        const downloadResponseModel = commandResponse?.fieldValues?.['DownloadFileResponseModel']
          ?? {
            fileId: commandResponse?.fieldValues?.['FileId'],
            fileName: commandResponse?.fieldValues?.['FileName']
          };
        if (!downloadResponseModel?.fileId) {
          this.isDownloading = false;
          return;
        }
        this.fileManagementService.downloadClientFileWithId(downloadResponseModel.fileId).pipe(take(1)).subscribe({
          next: payload => {
            this.fileManagementService.openSaveFileWindow(payload, downloadResponseModel.fileName);
            this.isDownloading = false;
          },
          error: () => { this.isDownloading = false; }
        });
      },
      error: () => { this.isDownloading = false; }
    });
  }

  // --- Support ticket (mapping exception) ---------------------------------

  openCreateSupportTicketDialog(): void {
    if (!this.reportWorkbookSetId) return;
    const command = new PlatformCommand('AddTaskDialogCommand', Api.TaskManager);
    command.setValue('ticketId', this.reportWorkbookSetId);
    command.title = 'Create Support Ticket';
    this.commandService.executePlatformCommand(command).pipe(take(1)).subscribe({
      next: () => this.loadValidationErrors(),
    });
  }

  // --- Row click → FlexSheet navigation -----------------------------------

  onRowClick(error: TransmissionError): void {
    this.rowClick.emit(error);
    const meta = error.metaData ?? {};
    const worksheetId = meta.WorksheetId ?? '';
    if (!worksheetId) return;
    if ((this.flexSheetService.getWorksheets()?.length ?? 0) === 0) return;

    const row = meta.Row ?? -1;
    const column = meta.Column ?? -1;
    let rowEnd = meta.RowEnd ?? -1;
    let columnEnd = meta.ColumnEnd ?? -1;

    const idx = this.flexSheetService.getWorksheets().findIndex(ws => ws.id === worksheetId);
    if (idx < 0) return;

    if (rowEnd === -1) rowEnd = row;
    if (columnEnd === -1) columnEnd = column;

    let cellAddress: any, callback: any;
    this.flexSheetNavigationService.executeNavigateToPaginatedPageByCellCommand(
      worksheetId, row , column, cellAddress, callback, rowEnd, columnEnd);
  }

  trackByError(_: number, error: TransmissionError): number {
    return error.errorSequenceId;
  }

  // --- Status getters -----------------------------------------------------

  get isReadyToValidate(): boolean {
    return !this.hasGenerated && !this.isLoading;
  }

  get hasValidationErrors(): boolean {
    return this.hasGenerated
      && !this.hasMappingException
      && this.isProcessComplete
      && this.validationErrors.length > 0;
  }

  get hasMappingIssue(): boolean {
    return this.hasGenerated
      && !this.isLoading
      && this.hasMappingException
      && !this.isProcessComplete;
  }

  get isAllChecksPassed(): boolean {
    return this.hasGenerated
      && !this.isLoading
      && this.isProcessComplete
      && !this.hasMappingException
      && this.validationErrors.length === 0;
  }

  get hasMappingException(): boolean {
    return this.validationErrors?.some(e => e.metaData?.HasExcelMappingException === true) ?? false;
  }

  get mappingExceptionError(): TransmissionError | null {
    return this.validationErrors?.find(e => e.metaData?.HasExcelMappingException === true) ?? null;
  }

  get mappingTicketDisplayId(): string {
    return this.mappingExceptionError?.metaData?.TicketDisplayId ?? '';
  }

  get mappingTicketUrl(): string {
    const ticketId = this.mappingExceptionError?.metaData?.TicketId;
    if (!ticketId) return '';
    const baseUrl = Configuration.getSupportUrl() || '';
    if (!baseUrl) return '';
    return `${baseUrl.replace(/\/$/, '')}/${ticketId}`;
  }

  // --- Display helpers ----------------------------------------------------

  displayErrorBodyText(error: TransmissionError): string {
    return (error.message?.trim() || error.exceptionDetail?.trim() || '').trimEnd();
  }

  getTooltipText(error: TransmissionError): string {
    return error.originalValidationMessage ?? '';
  }

  // errorType is 'Error' | 'Warning' | 'Unknown'; collapse to Warning vs Error.
  isWarning(error: TransmissionError): boolean {
    return (error.errorType ?? '').toLowerCase() === 'warning';
  }

  typeLabel(error: TransmissionError): string {
    return this.isWarning(error) ? 'Warning' : 'Error';
  }

  // "Sheet!A1" when both sheet + cell are present, else '' (used by the Cell column).
  getCellLabel(error: TransmissionError): string {
    const meta = error.metaData ?? {};
    const sheet = meta.SheetName ?? '';
    const a1    = meta.CellAddress ?? '';
    if (!sheet || !a1) return '';
    return `${sheet}!${a1}`;
  }

  // --- Normalisation ------------------------------------------------------

  private normalizeError(e: any): TransmissionError {
    const rawMeta = e?.MetaData ?? e?.metaData ?? {};
    const metaData: TransmissionErrorMetaData = {
      // Spread first so the explicit defaults below win over any null/undefined in rawMeta.
      ...rawMeta,
      WorkbookId: rawMeta.WorkbookId ?? rawMeta.workbookId ?? '',
      WorksheetId: rawMeta.WorksheetId ?? rawMeta.worksheetId ?? '',
      Row: Number(rawMeta.Row ?? rawMeta.row ?? -1),
      Column: Number(rawMeta.Column ?? rawMeta.column ?? -1),
      RowEnd: Number(rawMeta.RowEnd ?? rawMeta.rowEnd ?? -1),
      ColumnEnd: Number(rawMeta.ColumnEnd ?? rawMeta.columnEnd ?? -1),
      GridRow: Number(rawMeta.GridRow ?? rawMeta.gridRow ?? -1),
      GridColumn: Number(rawMeta.GridColumn ?? rawMeta.gridColumn ?? -1),
      SheetName: rawMeta.SheetName ?? rawMeta.sheetName ?? '',
      CellAddress: rawMeta.CellAddress ?? rawMeta.cellAddress ?? '',
      DataPath: rawMeta.DataPath ?? rawMeta.dataPath ?? '',
      HasExcelMappingException: rawMeta.HasExcelMappingException ?? rawMeta.hasExcelMappingException ?? false,
      TicketId: rawMeta.TicketId ?? rawMeta.ticketId ?? '',
      TicketDisplayId: rawMeta.TicketDisplayId ?? rawMeta.ticketDisplayId ?? '',
      TicketTitle: rawMeta.TicketTitle ?? rawMeta.ticketTitle ?? '',
    };
    return {
      id: e?.Id ?? e?.id ?? '',
      errorType: e?.ErrorType ?? e?.errorType ?? '',
      message: e?.Message ?? e?.message ?? '',
      exceptionDetail: e?.ExceptionDetail ?? e?.exceptionDetail ?? '',
      errorSequenceId: Number(e?.ErrorSequenceId ?? e?.errorSequenceId ?? 0) || 0,
      originalValidationMessage: e?.OriginalValidationMessage ?? e?.originalValidationMessage,
      metaData
    };
  }
}
